import { useEffect, useState } from "react";
import CrudPage from "@/components/app/CrudPage";
import api, { errMsg, formatTL, formatDate } from "@/lib/api";
import { FileSpreadsheet, X, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

export default function SalariesPage() {
  const [empOptions, setEmpOptions] = useState([]);
  const [empMap, setEmpMap] = useState({}); // id -> {name, type, role}
  const [showUpload, setShowUpload] = useState(false);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [autoCreate, setAutoCreate] = useState(true);
  const [reloadTick, setReloadTick] = useState(0);

  useEffect(() => {
    (async () => {
      const [d, p] = await Promise.all([api.get("/drivers"), api.get("/personnel")]);
      const opts = [
        ...d.data.map(x => ({ value: `driver:${x.id}`, label: `Şoför — ${x.name}` })),
        ...p.data.map(x => ({ value: `personnel:${x.id}`, label: `Personel — ${x.name}` })),
      ];
      setEmpOptions(opts);
      const map = {};
      d.data.forEach(x => { map[x.id] = { name: x.name, type: "driver", role: "Şoför" }; });
      p.data.forEach(x => { map[x.id] = { name: x.name, type: "personnel", role: x.role || "Personel" }; });
      setEmpMap(map);
    })();
  }, []);

  const runPreview = async () => {
    if (!file) { toast.error("Dosya seçin"); return; }
    const fd = new FormData();
    fd.append("file", file);
    fd.append("preview", "true");
    setLoading(true);
    try {
      const { data } = await api.post("/salary-payments/upload-monthly-xlsx", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(data);
      toast.success(`${data.totals?.sheets || 0} ay · ${data.totals?.rows || 0} kayıt okundu`);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setLoading(false); }
  };
  const runCommit = async () => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    fd.append("preview", "false");
    fd.append("auto_create_personnel", autoCreate ? "true" : "false");
    setLoading(true);
    try {
      const { data } = await api.post("/salary-payments/upload-monthly-xlsx", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${data.created} yeni · ${data.updated} güncelleme · ${data.created_personnel} yeni personel${data.skipped_no_match ? ` · ${data.skipped_no_match} atlandı` : ""}`);
      setShowUpload(false); setFile(null); setPreview(null);
      setReloadTick(t => t + 1);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setLoading(false); }
  };

  const uploadBtn = (
    <button
      data-testid="salaries-upload-btn"
      onClick={() => { setPreview(null); setFile(null); setAutoCreate(true); setShowUpload(true); }}
      className="flex items-center gap-2 px-3 py-2.5 text-xs font-semibold uppercase tracking-[0.15em] border border-[#0020C2] text-[#0020C2] hover:bg-[#0020C2] hover:text-white transition-colors"
    >
      <FileSpreadsheet className="w-3.5 h-3.5" /> Aylık Maaş Excel
    </button>
  );

  return (
    <>
      <CrudPage
        key={reloadTick}
        title="Maaş Ödemeleri"
        subtitle="Şoför ve personel için yapılan aylık maaş ödemeleri. Excel ile geçmiş dönem maaşlarını toplu yükleyebilirsiniz."
        endpoint="/salary-payments"
        testidPrefix="salaries"
        extraActions={uploadBtn}
        fields={[
          { key: "employee_type", label: "Çalışan Tipi", type: "select", required: true,
            options: [{ value: "driver", label: "Şoför" }, { value: "personnel", label: "Personel" }] },
          { key: "employee_id", label: "Çalışan", type: "select", required: true,
            options: empOptions.map(o => ({ value: o.value.split(":")[1], label: o.label })) },
          { key: "month", label: "Dönem (YYYY-MM)", type: "text", required: true },
          { key: "amount", label: "Tutar (₺)", type: "number", required: true },
          { key: "date", label: "Ödeme Tarihi", type: "date", required: true },
          { key: "notes", label: "Notlar", type: "textarea" },
        ]}
        columns={[
          { key: "date", label: "Tarih", render: (r) => formatDate(r.date) },
          { key: "employee_id", label: "Çalışan", render: (r) => {
            const info = empMap[r.employee_id];
            return info ? (
              <div>
                <div className="font-semibold">{info.name}</div>
                <div className="text-[10px] text-neutral-500">{info.role}</div>
              </div>
            ) : <span className="text-neutral-400 text-xs">Bilinmeyen kişi</span>;
          } },
          { key: "employee_type", label: "Tip", render: (r) => (empMap[r.employee_id]?.type || r.employee_type) === "driver" ? "Şoför" : "Personel" },
          { key: "month", label: "Dönem" },
          { key: "amount", label: "Tutar", align: "right", render: (r) => formatTL(r.amount) },
        ]}
      />

      {showUpload && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setShowUpload(false)}>
          <div className="bg-white w-full max-w-6xl rounded-md shadow-lg max-h-[92vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E5E5] sticky top-0 bg-white z-10">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-neutral-500">Toplu İçe Aktarım</div>
                <div className="font-heading font-bold text-lg">Aylık Maaş Excel Yükleme</div>
              </div>
              <button onClick={() => setShowUpload(false)} className="p-1.5 hover:bg-[#F5F5F5]" data-testid="salary-upload-close"><X className="w-4 h-4" /></button>
            </div>

            <div className="p-6 space-y-4">
              <div className="bg-[#F5F5F5] border border-[#E5E5E5] p-4 text-xs text-neutral-700 leading-relaxed">
                <div className="font-semibold text-[#0A0A0A] mb-1">📋 Excel formatı:</div>
                <div>Her <span className="font-semibold">sekme bir ay</span> (2024 ARALIK, 2025 OCAK, ...). Başlık satırı: <span className="num">PERSONEL · GÖREVİ · IBAN · MAAŞ · GÜN SAYISI · GÜNLÜK MAAŞ · AVANS · KESİNTİ · YOL PARASI · ÖDENECEK TUTAR · <b>ÖDENEN TUTAR</b> · KALAN TUTAR</span></div>
                <div className="mt-1 text-neutral-500">Sistem <span className="font-semibold">ÖDENEN TUTAR</span> kolonunu maaş ödemesi olarak kaydeder. GÖREVİ &quot;ŞOFÖR&quot; ise şoför tablosuna, diğer roller (HOSTES, OFİS ÇALIŞANI, K1-K11) personel tablosuna eşleşir. Türkçe karakter uyumlu isim eşleştirme yapılır. Aynı çalışan × ay için mevcut kayıt varsa güncellenir.</div>
              </div>

              <div>
                <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Excel Dosyası *</label>
                <input data-testid="salary-file-input" type="file" accept=".xlsx" onChange={e => { setFile(e.target.files?.[0] || null); setPreview(null); }} className="input mt-1.5" />
              </div>

              {!preview && (
                <div className="flex justify-end gap-2 pt-2">
                  <button data-testid="salary-preview-btn" onClick={runPreview} disabled={loading || !file}
                    className="px-4 py-2 text-xs uppercase tracking-widest font-semibold bg-[#0A0A0A] text-white hover:bg-[#0020C2] disabled:opacity-40">
                    {loading ? "Okunuyor..." : "Önizle"}
                  </button>
                </div>
              )}

              {preview && (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div className="border border-[#E5E5E5] p-3">
                      <div className="text-[10px] uppercase tracking-widest text-neutral-500">Ay</div>
                      <div className="num font-bold text-lg">{preview.totals?.sheets || 0}</div>
                    </div>
                    <div className="border border-[#E5E5E5] p-3">
                      <div className="text-[10px] uppercase tracking-widest text-neutral-500">Toplam Satır</div>
                      <div className="num font-bold text-lg">{preview.totals?.rows || 0}</div>
                    </div>
                    <div className="border border-[#E5E5E5] p-3">
                      <div className="text-[10px] uppercase tracking-widest text-neutral-500">Toplam Ödenen</div>
                      <div className="num font-bold text-base text-[#16A34A]">{formatTL(preview.totals?.total_paid || 0)}</div>
                    </div>
                    <div className="border border-[#E5E5E5] p-3">
                      <div className="text-[10px] uppercase tracking-widest text-neutral-500">Eşleşmeyen</div>
                      <div className="num font-bold text-lg text-[#DC2626]">{preview.totals?.unmatched || 0}</div>
                    </div>
                  </div>

                  {preview.totals?.unmatched > 0 && (
                    <div className="border border-[#F59E0B] bg-[#FEF3C7] p-3 text-xs flex items-start gap-2">
                      <AlertTriangle className="w-4 h-4 text-[#F59E0B] shrink-0 mt-0.5" />
                      <div>
                        <div className="font-semibold text-[#0A0A0A]">Sistemde bulunamayan personel/şoförler mevcut</div>
                        <div className="mt-1 text-neutral-500">Aşağıdaki &quot;Yeni personel/şoförleri otomatik oluştur&quot; seçeneği açıksa yeni personel olarak eklenirler. Kapalıysa atlanırlar.</div>
                      </div>
                    </div>
                  )}

                  <div className="border border-[#E5E5E5] rounded overflow-hidden max-h-[380px] overflow-y-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-[#FAFAFA] sticky top-0">
                        <tr>
                          {["Ay","Sekme","Kişi","Toplam Ödenen","Eşleşmeyen"].map((h, i) => (
                            <th key={h} className={`px-3 py-2 text-[10px] uppercase tracking-widest text-neutral-500 font-semibold ${i >= 2 ? "text-right" : "text-left"}`}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {(preview.sheets || []).map((sh, i) => {
                          const unmatched = (sh.rows || []).filter(r => !r.matched).length;
                          return (
                            <tr key={i} className={`border-t border-[#F5F5F5] ${sh.skipped ? "text-neutral-400" : ""}`}>
                              <td className="px-3 py-1.5 num font-semibold">{sh.month || "-"}</td>
                              <td className="px-3 py-1.5 num">{sh.sheet} {sh.skipped && <span className="text-[10px] text-[#DC2626]">({sh.skipped})</span>}</td>
                              <td className="px-3 py-1.5 num text-right">{(sh.rows || []).length}</td>
                              <td className="px-3 py-1.5 num text-right font-semibold text-[#16A34A]">{formatTL(sh.sheet_total_paid || 0)}</td>
                              <td className={`px-3 py-1.5 num text-right ${unmatched > 0 ? "text-[#F59E0B] font-semibold" : ""}`}>{unmatched}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  <div className="flex items-center gap-2 pt-2">
                    <input id="auto-create" type="checkbox" checked={autoCreate} onChange={e => setAutoCreate(e.target.checked)} data-testid="salary-autocreate" />
                    <label htmlFor="auto-create" className="text-xs text-neutral-700 cursor-pointer">
                      <span className="font-semibold">Eşleşmeyen kişileri otomatik olarak personel olarak oluştur</span> <span className="text-neutral-500">(kapalıyken atlanır)</span>
                    </label>
                  </div>

                  <div className="flex justify-between items-center gap-2 pt-4 border-t border-[#E5E5E5]">
                    <button onClick={() => setPreview(null)} className="px-4 py-2 text-xs uppercase tracking-widest font-semibold border border-[#E5E5E5] hover:bg-[#F5F5F5]">← Farklı Dosya</button>
                    <div className="flex gap-2">
                      <button onClick={() => setShowUpload(false)} className="px-4 py-2 text-xs uppercase tracking-widest font-semibold border border-[#E5E5E5] hover:bg-[#F5F5F5]">Vazgeç</button>
                      <button data-testid="salary-commit-btn" onClick={runCommit} disabled={loading}
                        className="px-4 py-2 text-xs uppercase tracking-widest font-semibold bg-[#16A34A] text-white hover:bg-[#0F7A36] disabled:opacity-40">
                        {loading ? "Kaydediliyor..." : `✓ Sisteme İşle`}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
